import { useEffect } from "react";

import { articleFilenameToSlugAtom } from "@/src/state/articleFilenameToSlugAtom";
import { articleListAtom } from "@/src/state/articleListAtom";
import { articlesAtom } from "@/src/state/articlesAtom";

import type { Article } from "@/src/utils/blogUtils";

import { useAtomValue, useSetAtom, type WritableAtom } from "jotai";
import { useHydrateAtoms } from "jotai/react/utils";

interface HydratableArticleData {
  filenames?: string[];
  filenameToSlug?: Record<string, string>;
  articles?: Record<string, Article>;
}

// Hydrates the shared article atoms from route loader data.
//
// `useHydrateAtoms` writes during render when atoms are still empty, which is
// required for SSR/prerender so the initial HTML carries real article data.
//
// Article atoms are persisted in localStorage and may already hold data from
// `useGetArticles` (e.g. on the home page) or a previous visit. A render-phase
// write in that case can update still-mounted article consumers and trigger
// React's "Cannot update a component while rendering a different component"
// error. We therefore skip the render-phase write when atoms are populated and
// instead sync loader data in a route-scoped effect whenever `loaderData`
// changes, keeping blog navigations aligned with the route loader output.
export const useHydrateArticleAtoms = (
  loaderData: HydratableArticleData | undefined
) => {
  const setArticleList = useSetAtom(articleListAtom);
  const setFilenameToSlug = useSetAtom(articleFilenameToSlugAtom);
  const setArticles = useSetAtom(articlesAtom);
  const alreadyPopulated = Object.keys(useAtomValue(articlesAtom)).length > 0;

  useHydrateAtoms(
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    new Map<WritableAtom<any, any[], any>, unknown>(
      alreadyPopulated
        ? []
        : [
            [articleListAtom, loaderData?.filenames ?? []],
            [articleFilenameToSlugAtom, loaderData?.filenameToSlug ?? {}],
            [articlesAtom, loaderData?.articles ?? {}]
          ]
    )
  );

  useEffect(() => {
    const articles = loaderData?.articles;
    if (!articles || Object.keys(articles).length === 0) {
      return;
    }

    setArticleList(loaderData.filenames ?? []);
    setFilenameToSlug(loaderData.filenameToSlug ?? {});
    setArticles(articles);
  }, [loaderData, setArticleList, setFilenameToSlug, setArticles]);
};
